import { LopDetailNote } from "@/components/payslip/LopDetailNote";
import { AddIcon } from "@/components/icons/action-icons";
import { ExcludeItemButton } from "./_components/ExcludeItemButton";
import { useEffect, useMemo, useRef, useState } from "react";
import { useBlocker, useNavigate, useParams } from "react-router-dom";
import {
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Download,
  Lock,
  MoreHorizontal,
  X,
} from "lucide-react";
import { format } from "date-fns";
import ReactCountryFlag from "react-country-flag";
import toast from "react-hot-toast";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { PermissionGuard } from "@/components/permission-guard";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ROUTES } from "@/constants/routes";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding, formatDateDmy } from "@/utils/helper";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { cn } from "@/utils/cn";
import {
  useGetItemDetailQuery,
  useGetRunDetailQuery,
  useUpdateItemMutation,
  useHoldItemMutation,
  useReleaseItemMutation,
  type IPayrollRunItemLine,
  type IPayslipPreview,
  type IStatutoryLine,
} from "@/services/(company)/payrollRunsApi";
import { useDownloadPayslip } from "@/services/(company)/payslipDownload";
import {
  PaymentMethodTypeEnum,
  PayrollRunItemLineSourceEnum,
  PayrollRunStatusEnum,
} from "@/utils/enums/payroll.enum";
import { ChangeHistory } from "./_components/ChangeHistory";
import { SaveReasonDialog } from "./_components/SaveReasonDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGetItemAuditLogsQuery } from "@/services/(company)/payrollRunsApi";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { PayslipPeriodRows } from "@/components/payslip/PayslipPeriodRows";

const READ_ONLY_STATUSES = new Set([
  PayrollRunStatusEnum.APPROVED,
  PayrollRunStatusEnum.PROCESSING,
  PayrollRunStatusEnum.COMPLETED,
]);

const buildPaymentMethodOptions = (
  t: TFunction,
): {
  value: PaymentMethodTypeEnum;
  label: string;
}[] => [
  { value: PaymentMethodTypeEnum.WPS, label: "WPS" },
  {
    value: PaymentMethodTypeEnum.BANK_TRANSFER,
    label: t("company:payrolls.bank_transfer"),
  },
  { value: PaymentMethodTypeEnum.CHEQUE, label: t("company:payrolls.cheque") },
  { value: PaymentMethodTypeEnum.CASH, label: t("company:payrolls.cash") },
];

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

const toNumber = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : v;
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (n: number) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

type LineKey = "earnings" | "benefits" | "deductions";

const LOP_REFERENCE_TYPE = "LOP_SYSTEM";

interface FormState {
  holdSalary: boolean;
  paymentMethod: PaymentMethodTypeEnum | "";
  bankName: string;
  accountNumber: string;
  routingNumber: string;
  wpsPersonId: string;
  earnings: IPayrollRunItemLine[];
  benefits: IPayrollRunItemLine[];
  deductions: IPayrollRunItemLine[];
  /** Unpaid (LOP) days; changing it recalculates Loss of Pay on save (D4). */
  unpaidLeaveDays: string;
}

const EMPTY_FORM: FormState = {
  holdSalary: false,
  paymentMethod: "",
  bankName: "",
  accountNumber: "",
  routingNumber: "",
  wpsPersonId: "",
  earnings: [],
  benefits: [],
  deductions: [],
  unpaidLeaveDays: "0",
};

const Section = ({
  title,
  rightSlot,
  children,
  bottomFlush,
}: {
  title: string;
  rightSlot?: React.ReactNode;
  children: React.ReactNode;
  bottomFlush?: boolean;
}) => {
  const [open, setOpen] = useState(true);
  return (
    <div
      className={cn(
        "rounded-xs border border-border bg-white overflow-hidden",
        bottomFlush && "rounded-b-none border-b-0",
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex items-center justify-between gap-3 w-full px-5 py-3",
          open && "border-b border-border",
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">{title}</span>
          {rightSlot}
        </div>
        {open ? (
          <ChevronUp size={16} className="text-primary" />
        ) : (
          <ChevronDown size={16} className="text-gray-400" />
        )}
      </button>
      {open && <div className="px-5 py-4">{children}</div>}
    </div>
  );
};

const buildEarningExamples = (t: TFunction) => [
  t("company:payrolls.basic_salary"),
  t("company:payrolls.housing_allowance"),
  t("company:payrolls.bonus"),
  t("company:payrolls.transport_allowance"),
];
const buildDeductionExamples = (t: TFunction) => [
  t("company:payrolls.late_penalty"),
  t("company:payrolls.loan_repayment"),
  t("company:payrolls.salary_advance"),
  t("company:payrolls.unpaid_leave"),
];

const LineRow = ({
  line,
  currencyCode,
  tone,
  onChange,
  onRemove,
  readOnly,
}: {
  line: IPayrollRunItemLine;
  currencyCode: string;
  tone: "earning" | "deduction";
  onChange: (next: IPayrollRunItemLine) => void;
  onRemove: () => void;
  readOnly?: boolean;
}) => {
  const { t } = useTranslation(["company", "common"]);
  const placeholder = useMemo(() => {
    const pool =
      tone === "earning" ? buildEarningExamples(t) : buildDeductionExamples(t);
    return `e.g. ${pool[Math.floor(Math.random() * pool.length)]}`;
  }, [tone]);

  // For auto-applied penalties, surface how many late/early records drove it.
  const penaltyInfo = useMemo(() => {
    if (line.source !== PayrollRunItemLineSourceEnum.PENALTY) return null;
    const m = line.metadata ?? {};
    const occ = Number(m.occurrences);
    if (!Number.isFinite(occ) || occ <= 0) return null;
    const charged = Number(m.chargeableOccurrences);
    const base =
      m.penaltyType === "EARLY_CHECKOUT"
        ? t("company:payrolls.early_checkout_count", { count: occ })
        : t("company:payrolls.late_arrival_count", { count: occ });
    return Number.isFinite(charged) && charged !== occ
      ? t("company:payrolls.penalty_occurrences_charged", { base, charged })
      : base;
  }, [line.source, line.metadata, t]);

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xs px-3 py-2",
        tone === "earning" ? "bg-primary-light/20" : "bg-red-50",
        !line.isActive && "opacity-50",
      )}
    >
      <span
        className={cn(
          "size-2 rounded-full shrink-0",
          tone === "earning" ? "bg-yellow-500" : "bg-red-500",
        )}
      />
      <div className="flex-1 min-w-0 flex flex-col">
        {readOnly ? (
          <span className="text-sm font-medium text-gray-900 truncate">
            {line.name || "—"}
          </span>
        ) : (
          <input
            type="text"
            value={line.name}
            onChange={(e) => onChange({ ...line, name: e.target.value })}
            placeholder={placeholder}
            className="w-full bg-transparent text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:outline-hidden"
          />
        )}
        {penaltyInfo && (
          <span className="text-[10px] text-muted-foreground">
            {penaltyInfo}
          </span>
        )}
      </div>
      {line.source === PayrollRunItemLineSourceEnum.PENALTY && (
        <span
          className="shrink-0 rounded-full bg-warning-muted px-2 py-0.5 text-[10px] font-semibold text-warning"
          title={
            typeof line.metadata?.occurrences === "number"
              ? t("company:payrolls.occurrences_detected", {
                  count: line.metadata.occurrences,
                })
              : t("company:payrolls.auto_applied_penalty")
          }
        >
          {t("company:payrolls.penalty")}
        </span>
      )}
      <span className="text-[11px] text-gray-400">{currencyCode}</span>
      {readOnly ? (
        <span className="w-28 text-sm font-semibold text-end text-gray-900">
          {formatNumber(toNumber(line.amount))}
        </span>
      ) : (
        <input
          type="number"
          value={line.amount}
          onChange={(e) => onChange({ ...line, amount: e.target.value })}
          className="h-9 w-28 border border-input rounded-xs px-2 text-sm text-end focus:outline-hidden focus:ring-2 focus:ring-ring bg-white"
        />
      )}
      {!readOnly && (
        <>
          <Switch
            checked={line.isActive}
            onCheckedChange={(v) => onChange({ ...line, isActive: v })}
          />
          <button
            type="button"
            onClick={onRemove}
            className="size-7 rounded-full border border-red-200 bg-white text-red-500 flex items-center justify-center hover:bg-red-50"
          >
            <X size={14} />
          </button>
        </>
      )}
    </div>
  );
};

const LinesSection = ({
  label,
  lines,
  currencyCode,
  tone,
  onChange,
  bottomFlush,
  readOnly,
}: {
  label: string;
  lines: IPayrollRunItemLine[];
  currencyCode: string;
  tone: "earning" | "deduction";
  onChange: (next: IPayrollRunItemLine[]) => void;
  bottomFlush?: boolean;
  readOnly?: boolean;
}) => {
  const { t } = useTranslation(["company", "common"]);
  const updateLine = (idx: number, next: IPayrollRunItemLine) => {
    onChange(lines.map((l, i) => (i === idx ? next : l)));
  };
  const removeLine = (idx: number) => {
    onChange(lines.filter((_, i) => i !== idx));
  };
  const addLine = () => {
    onChange([
      ...lines,
      { name: "", amount: 0, isActive: true } as IPayrollRunItemLine,
    ]);
  };

  return (
    <Section title={label} bottomFlush={bottomFlush}>
      <div className="flex flex-col gap-2">
        {lines.length === 0 ? (
          <div className="text-xs text-gray-400 py-4 text-center">
            {t("company:payrolls.no_items")}
          </div>
        ) : (
          lines.map((l, idx) => (
            <LineRow
              key={l.id ?? `new-${idx}`}
              line={l}
              currencyCode={currencyCode}
              tone={tone}
              onChange={(next) => updateLine(idx, next)}
              onRemove={() => removeLine(idx)}
              readOnly={readOnly}
            />
          ))
        )}

        {!readOnly && (
          <div className="pt-2">
            <button
              type="button"
              onClick={addLine}
              className="inline-flex items-center gap-2 rounded-xs border border-primary/30 bg-primary-light/30 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary-light/50"
            >
              <AddIcon size={14} className="text-primary" />
              {t("company:payrolls.add_line_of_type", { type: label })}
            </button>
          </div>
        )}
      </div>
    </Section>
  );
};

const TotalsRow = ({
  label,
  currencyCode,
  amount,
  topFlush,
}: {
  label: string;
  currencyCode: string;
  amount: number;
  topFlush?: boolean;
}) => (
  <div
    className={cn(
      "flex items-center justify-between rounded-xs border border-border bg-white px-5 py-3",
      topFlush && "rounded-t-none border-t border-t-border mt-0",
    )}
  >
    <span className="text-sm font-semibold text-gray-700">{label}</span>
    <span className="text-sm font-bold text-gray-900">
      {currencyCode} {formatNumber(amount)}
    </span>
  </div>
);

const safeFormat = (iso: string | null | undefined, fmt: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : format(d, fmt);
};

const PayslipField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[9px] uppercase tracking-wider text-gray-400">
      {label}
    </span>
    <span className="text-[11px] font-semibold text-gray-900">{value}</span>
  </div>
);

const PayslipPreviewCard = ({ preview }: { preview: IPayslipPreview }) => {
  const { t } = useTranslation(["company", "common"]);
  const { company, employee, currency, earnings, deductions } = preview;
  const logoUrl = company.logo?.trim() || null;
  return (
    <div className="rounded-xs border border-border bg-white text-gray-700 flex flex-col overflow-hidden h-full">
      {/* Brand strip */}
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={company.name}
              className="h-9 max-w-[165px] object-contain"
            />
          ) : (
            <span className="text-sm font-bold text-gray-900">
              {company.name}
            </span>
          )}
        </div>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
          {t("nav:tabs.payslip")}
        </span>
      </div>

      {/* Pink divider */}
      <div className="h-px bg-linear-to-r from-pink-400 via-pink-500 to-rose-500" />

      {/* Company + payslip details */}
      <div className="grid grid-cols-2 gap-6 bg-gray-50 px-5 py-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
            {t("company:payrolls.company")}
          </span>
          <span className="text-sm font-bold text-gray-900">
            {company.name}
          </span>
          {company.address && (
            <span className="text-[11px] text-gray-500">
              {typeof company.address === "string"
                ? company.address
                : [
                    (company.address as any).street,
                    (company.address as any).city,
                    (company.address as any).state,
                  ]
                    .filter(Boolean)
                    .join(", ")}
            </span>
          )}
          {(company.email || company.phone) && (
            <span className="text-[11px] text-gray-500">
              {company.email}
              {company.email && company.phone && "  |  "}
              {company.phone}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1 text-end">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
            {t("company:payrolls.payslip_details")}
          </span>
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
            <span className="text-gray-500 text-start">
              {t("company:payrolls.pay_period")}
            </span>
            <span className="font-semibold text-gray-900 text-end">
              {preview.payPeriod}
            </span>
            <PayslipPeriodRows preview={preview} />
            <span className="text-gray-500 text-start">
              {t("company:payrolls.payment_date")}
            </span>
            <span className="font-semibold text-gray-900 text-end">
              {safeFormat(preview.paymentDate, "dd MMM yyyy")}
            </span>
            <span className="text-gray-500 text-start">
              {t("company:payrolls.payslip_no")}
            </span>
            <span className="font-semibold text-gray-900 text-end">
              {preview.payslipNo}
            </span>
          </div>
        </div>
      </div>

      {/* Employee info */}
      <div className="px-5 pt-4 pb-4">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
          {t("company:payrolls.employee_information")}
        </span>
        <div className="grid grid-cols-4 gap-x-4 gap-y-3 mt-3">
          <PayslipField
            label={t("company:payrolls.employee_name")}
            value={employee.fullName}
          />
          <PayslipField
            label={t("company:payrolls.employee_id")}
            value={employee.employeeCode}
          />
          <PayslipField
            label={t("common:labels.designation")}
            value={employee.designation ?? "—"}
          />
          <PayslipField
            label={t("common:labels.department")}
            value={employee.department ?? "—"}
          />
          <PayslipField
            label={t("company:payrolls.joining_date")}
            value={safeFormat(employee.joiningDate, "dd MMM yyyy")}
          />
          <PayslipField
            label={t("company:payrolls.payment_mode")}
            value={preview.paymentMode ?? "—"}
          />
          <PayslipField
            label={t("company:payrolls.bank_name")}
            value={employee.bankName ?? "—"}
          />
          <PayslipField
            label={t("company:payrolls.bank_a_c_no")}
            value={
              <span className="truncate block">
                {employee.accountNumber ?? "—"}
              </span>
            }
          />
        </div>
      </div>

      {/* Earnings + deductions tables */}
      <div className="grid grid-cols-2 gap-3 px-5">
        <div className="rounded-xs border border-border overflow-hidden flex flex-col">
          <div className="flex items-center justify-between bg-gray-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            <span>{t("company:payrolls.earnings")}</span>
            <span>
              {t("company:payrolls.amount_with_currency", {
                currency: currency?.code,
              })}
            </span>
          </div>
          <div className="flex flex-col flex-1">
            {earnings.length === 0 ? (
              <span className="px-3 py-2 text-[11px] text-gray-400">
                {t("company:payrolls.no_earnings")}
              </span>
            ) : (
              earnings.map((e, idx) => (
                <div
                  key={`${e.name}-${idx}`}
                  className="flex items-center justify-between gap-2 px-3 py-1.5 text-[11px]"
                >
                  <span className="truncate text-gray-700">{e.name}</span>
                  <span className="font-semibold text-gray-900">
                    {formatNumber(toNumber(e.amount))}
                  </span>
                </div>
              ))
            )}
          </div>
          <div className="flex items-center justify-between border-t border-border px-3 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              {t("company:payrolls.total_earnings")}
            </span>
            <span className="text-[11px] font-bold text-primary">
              {formatNumber(preview.totalEarnings)}
            </span>
          </div>
        </div>
        <div className="rounded-xs border border-border overflow-hidden flex flex-col">
          <div className="flex items-center justify-between bg-gray-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            <span>{t("company:payrolls.deductions")}</span>
            <span>
              {t("company:payrolls.amount_with_currency", {
                currency: currency?.code,
              })}
            </span>
          </div>
          <div className="flex flex-col flex-1">
            {deductions.length === 0 ? (
              <span className="px-3 py-2 text-[11px] text-gray-400">
                {t("company:payrolls.no_deductions")}
              </span>
            ) : (
              deductions.map((d, idx) => (
                <div
                  key={`${d.name}-${idx}`}
                  className="flex items-center justify-between gap-2 px-3 py-1.5 text-[11px]"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-gray-700">{d.name}</span>
                    <LopDetailNote detail={d.lopDetail} />
                  </span>
                  <span className="font-semibold text-gray-900">
                    {formatNumber(toNumber(d.amount))}
                  </span>
                </div>
              ))
            )}
          </div>
          <div className="flex items-center justify-between border-t border-border px-3 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              {t("company:payrolls.total_deductions")}
            </span>
            <span className="text-[11px] font-bold text-red-500">
              {formatNumber(preview.totalDeductions)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1" />

      {/* Net salary banner */}
      <div className="bg-gray-50 px-5 py-4 flex items-center justify-between border-t border-border">
        <div className="flex flex-col">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">
            {t("company:payrolls.net_salary")}
          </span>
          <span className="text-[10px] text-gray-500">
            {t("company:payrolls.total_take_home_pay_after_all_deductions")}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-[10px] font-semibold uppercase text-gray-400">
            {currency?.code}
          </span>
          <span className="text-xl font-bold text-gray-900">
            {formatNumber(preview.netSalary)}
          </span>
        </div>
      </div>

      {/* Signatures */}
      <div className="grid grid-cols-2 gap-6 px-5 pt-6 pb-4">
        <div className="flex flex-col">
          <div className="h-7 border-b border-gray-300" />
          <span className="text-[11px] text-gray-500 mt-1">
            {t("company:payrolls.employer_signature")}
          </span>
        </div>
        <div className="flex flex-col">
          <div className="h-7 border-b border-gray-300" />
          <span className="text-[11px] text-gray-500 mt-1">
            {t("company:payrolls.employee_signature")}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="h-px bg-linear-to-r from-primary/40 via-primary to-primary/40" />
      <div className="px-5 py-2 text-center text-[10px] text-gray-500">
        {t("company:payslip.company_generated_on", {
          company: company.name,
          date: formatDateDmy(new Date()),
        })}
      </div>
    </div>
  );
};

const StatutoryRow = ({
  line,
  currencyCode,
}: {
  line: IStatutoryLine;
  currencyCode: string;
}) => {
  const { t } = useTranslation(["company", "common"]);
  const isEmployee = line.party === "EMPLOYEE";
  const isEmployer = line.party === "EMPLOYER";
  const isGovt = line.party === "GOVERNMENT";
  const muted = isEmployer || isGovt;
  const formula =
    line.basisAmount && line.rate
      ? t("company:payrolls.pensionable_salary_formula", {
          rate: line.rate,
          amount: formatNumber(toNumber(line.basisAmount)),
        })
      : null;

  return (
    <div
      className={cn(
        "flex items-start justify-between gap-3 px-3 py-2.5 border-t border-border first:border-t-0",
        muted && "opacity-70",
      )}
    >
      <div className="flex items-start gap-2 min-w-0">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="mt-0.5 shrink-0 text-gray-400 cursor-help">
                <Lock size={13} />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">
              {t("company:payrolls.statutory_tooltip")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className={cn(
                "shrink-0 rounded-xs px-1.5 py-0.5 text-[9px] font-semibold uppercase",
                isEmployee && "bg-primary/10 text-primary",
                isEmployer && "bg-warning-muted text-yellow-700",
                isGovt && "bg-emerald-100 text-emerald-700",
              )}
            >
              {isEmployee
                ? t("common:labels.employee")
                : isEmployer
                  ? t("company:payrolls.employer")
                  : t("company:payrolls.government")}
            </span>
            <span className="text-sm text-gray-900 truncate">{line.name}</span>
            {muted && (
              <span className="shrink-0 text-[9px] text-gray-400 border border-border rounded-xs px-1 py-0.5 uppercase font-semibold">
                {t("company:payrolls.info_only")}
              </span>
            )}
          </div>
          {formula && (
            <span className="text-[10px] text-gray-400 mt-0.5">{formula}</span>
          )}
        </div>
      </div>
      <span
        className={cn(
          "text-sm font-semibold shrink-0",
          isEmployee ? "text-red-600" : "text-gray-500",
        )}
      >
        {isEmployee ? "−" : "+"} {currencyCode}{" "}
        {formatNumber(toNumber(line.amount))}
      </span>
    </div>
  );
};

export const EditPayrollRunItem = () => {
  const { t } = useTranslation(["company", "common"]);
  usePageTitle(withBranding(t("company:payrolls.edit_payroll_item")));
  const navigate = useNavigate();
  const { runId: runIdParam, itemId: itemIdParam } = useParams<{
    runId: string;
    itemId: string;
  }>();
  const runId = Number(runIdParam);
  const itemId = Number(itemIdParam);

  const skip = !runId || !itemId || isNaN(runId) || isNaN(itemId);

  const { data: itemResponse, isLoading } = useGetItemDetailQuery(
    { runId, itemId },
    { skip },
  );
  const detail = itemResponse?.data;

  const { data: runResponse } = useGetRunDetailQuery(runId, { skip });
  const runStatus = runResponse?.data?.status as
    PayrollRunStatusEnum | undefined;
  /** Paid out or on its way: the payslip exists and can be downloaded. */
  const isProcessed = runStatus ? READ_ONLY_STATUSES.has(runStatus) : false;
  /**
   * R25: editable only while the run is IN_REVIEW — the one status
   * `updateItem` accepts. The form used to be editable in every status but
   * the processed three (DRAFT, GENERATING, FAILED, CANCELLED), so HR filled
   * it in and Save came back 400. Read-only while the run is still loading,
   * too, rather than briefly offering a form that may not save.
   */
  const isReadOnly = runStatus !== PayrollRunStatusEnum.IN_REVIEW;
  /** Hold has its own, wider rule on the API: anything but COMPLETED/CANCELLED. */
  const canToggleHold =
    !!runStatus && !isProcessed && runStatus !== PayrollRunStatusEnum.CANCELLED;

  const [updateItem, { isLoading: isSaving }] = useUpdateItemMutation();
  const [holdItem, { isLoading: isHolding }] = useHoldItemMutation();
  const [releaseItem, { isLoading: isReleasing }] = useReleaseItemMutation();
  const isTogglingHold = isHolding || isReleasing;
  const { download: downloadPayslip, isDownloading } = useDownloadPayslip();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [savedForm, setSavedForm] = useState<FormState>(EMPTY_FORM);

  useEffect(() => {
    if (!detail) return;
    const cleanLines = (lines: IPayrollRunItemLine[]) =>
      lines.map((l) => ({ ...l, amount: toNumber(l.amount) }));
    // Stable sort: Basic-typed earning floats to the top of the form so the
    // user sees the same order shown in the payslip preview / PDF.
    const sortBasicFirst = (lines: IPayrollRunItemLine[]) => {
      const isBasic = (l: IPayrollRunItemLine) =>
        typeof l.metadata?.earningTypeName === "string" &&
        l.metadata.earningTypeName.trim().toLowerCase() === "basic";
      return [...lines].sort(
        (a, b) => (isBasic(a) ? 0 : 1) - (isBasic(b) ? 0 : 1),
      );
    };
    const lopFromDetail = toNumber(detail.lopAmount);
    const unpaidDaysFromDetail = toNumber(detail.unpaidLeaveDays);
    const baseDeductions = cleanLines(detail.deductions ?? []);
    const hasLopLine = baseDeductions.some(
      (l) => l.referenceType === LOP_REFERENCE_TYPE,
    );
    const deductionsWithLop =
      lopFromDetail > 0 && !hasLopLine
        ? [
            ...baseDeductions,
            {
              name:
                unpaidDaysFromDetail > 0
                  ? t("company:payrolls.loss_of_pay_days", {
                      count: unpaidDaysFromDetail,
                    })
                  : t("company:payrolls.loss_of_pay"),
              amount: lopFromDetail,
              isActive: true,
              referenceType: LOP_REFERENCE_TYPE,
            },
          ]
        : baseDeductions;

    const next: FormState = {
      holdSalary: Boolean(detail.holdSalary),
      paymentMethod: detail.paymentMethod ?? "",
      bankName: detail.bankName ?? "",
      accountNumber: detail.accountNumber ?? "",
      routingNumber: detail.routingNumber ?? "",
      wpsPersonId: detail.wpsPersonId ?? "",
      earnings: sortBasicFirst(cleanLines(detail.earnings ?? [])),
      benefits: cleanLines(detail.benefits ?? []),
      deductions: deductionsWithLop,
      unpaidLeaveDays: String(toNumber(detail.unpaidLeaveDays)),
    };
    setForm(next);
    setSavedForm(next);
  }, [detail]);

  const isDirty = useMemo(
    () => !isReadOnly && JSON.stringify(form) !== JSON.stringify(savedForm),
    [isReadOnly, form, savedForm],
  );

  const isSavingRef = useRef(false);
  const blocker = useBlocker(() => isDirty && !isSavingRef.current);

  const { data: auditLogsData } = useGetItemAuditLogsQuery(
    { runId, itemId },
    { skip },
  );
  const auditLogCount = auditLogsData?.data?.length ?? 0;

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleBack = () => navigate(ROUTES.HOME.COMPANY.PAYROLL.RUN(runId));

  const normalizeLines = (lines: IPayrollRunItemLine[]) =>
    lines.map((l) => ({
      ...l,
      amount: Math.max(0, toNumber(l.amount)),
    }));

  const [holdReasonOpen, setHoldReasonOpen] = useState(false);

  const performHold = async (reason: string) => {
    if (skip || isTogglingHold) return;
    setForm((f) => ({ ...f, holdSalary: true }));
    try {
      await toast.promise(holdItem({ runId, itemId, reason }).unwrap(), {
        loading: t("company:payrolls.holding_salary"),
        success: t("company:payrolls.salary_held"),
        error: (err: any) =>
          err?.data?.message ??
          err?.message ??
          t("company:payrolls.failed_to_hold_salary"),
      });
      setSavedForm((f) => ({ ...f, holdSalary: true }));
      setHoldReasonOpen(false);
    } catch {
      setForm((f) => ({ ...f, holdSalary: false }));
    }
  };

  const handleToggleHold = async (next: boolean) => {
    if (skip || isTogglingHold) return;
    if (next) {
      setHoldReasonOpen(true);
      return;
    }
    setForm((f) => ({ ...f, holdSalary: false }));
    try {
      await toast.promise(releaseItem({ runId, itemId }).unwrap(), {
        loading: t("company:payrolls.releasing_salary"),
        success: t("company:payrolls.salary_released"),
        error: (err: any) =>
          err?.data?.message ??
          err?.message ??
          t("company:payrolls.failed_to_release_salary"),
      });
      setSavedForm((f) => ({ ...f, holdSalary: false }));
    } catch {
      setForm((f) => ({ ...f, holdSalary: true }));
    }
  };

  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);

  const handleSaveClick = () => {
    if (skip || !isDirty) return;
    setReasonDialogOpen(true);
  };

  const handleSaveWithReason = async (reason: string) => {
    if (skip) return;
    try {
      const lopLine = form.deductions.find(
        (l) => l.referenceType === LOP_REFERENCE_TYPE && l.isActive,
      );
      const lopAmountFromLine = lopLine
        ? Math.max(0, toNumber(lopLine.amount))
        : 0;
      const deductionsWithoutLop = form.deductions.filter(
        (l) => l.referenceType !== LOP_REFERENCE_TYPE,
      );
      // New unpaid days: the server works Loss of Pay out from them at this
      // run's day rate, so the typed amount is not sent with them (D4).
      const daysChanged =
        toNumber(form.unpaidLeaveDays) !== toNumber(savedForm.unpaidLeaveDays);

      await updateItem({
        runId,
        itemId,
        data: {
          paymentMethod: form.paymentMethod || undefined,
          bankName: form.bankName || null,
          accountNumber: form.accountNumber || null,
          routingNumber: form.routingNumber || null,
          wpsPersonId: form.wpsPersonId || null,
          earnings: normalizeLines(form.earnings),
          benefits: normalizeLines(form.benefits),
          deductions: normalizeLines(deductionsWithoutLop),
          ...(daysChanged
            ? { unpaidLeaveDays: Math.max(0, toNumber(form.unpaidLeaveDays)) }
            : { lopAmount: lopAmountFromLine }),
          reason,
        },
      }).unwrap();
      setSavedForm(form);
      isSavingRef.current = true;
      setReasonDialogOpen(false);
      toast.success(t("company:payrolls.item_updated"));
      navigate(ROUTES.HOME.COMPANY.PAYROLL.RUN(runId));
    } catch (err: any) {
      const msg = err?.data?.message;
      toast.error(
        Array.isArray(msg)
          ? msg.join(", ")
          : (msg ?? err?.message ?? t("company:payrolls.failed_to_save")),
      );
    }
  };

  const updateLines = (key: LineKey) => (next: IPayrollRunItemLine[]) =>
    setForm((f) => ({ ...f, [key]: next }));

  const sumLines = (lines: IPayrollRunItemLine[]) =>
    lines.reduce((acc, l) => acc + (l.isActive ? toNumber(l.amount) : 0), 0);

  const totalEarnings = useMemo(() => sumLines(form.earnings), [form.earnings]);
  const totalBenefits = useMemo(() => sumLines(form.benefits), [form.benefits]);
  const totalDeductions = useMemo(
    () => sumLines(form.deductions),
    [form.deductions],
  );
  const totalStatutoryEmployee = toNumber(detail?.totalStatutoryEmployee);

  /**
   * Deductions above half the wage earned (D4; UAE Decree-Law 33/2021,
   * Art. 25). Loss of Pay is days not worked, so it lowers the wage rather
   * than counting as a deduction — the same rule as the run's warning.
   */
  const deductionShareOverHalf = useMemo(() => {
    const lop = form.deductions
      .filter((l) => l.referenceType === LOP_REFERENCE_TYPE && l.isActive)
      .reduce((sum, l) => sum + toNumber(l.amount), 0);
    const wage = totalEarnings + totalBenefits - lop;
    const deductions = totalDeductions - lop;
    if (!(wage > 0) || !(deductions > 0) || deductions / wage <= 0.5)
      return null;
    return Math.round((deductions / wage) * 1000) / 10;
  }, [form.deductions, totalEarnings, totalBenefits, totalDeductions]);

  const livePreview = useMemo<IPayslipPreview | null>(() => {
    if (!detail?.payslipPreview) return null;
    const base = detail.payslipPreview;

    // Mirrors backend buildPayslipPreview. Basic salary is a regular earning
    // line tagged with metadata.earningTypeName === 'Basic' and hoisted to the
    // top via stable sort — no hard-coded "Basic Salary" entry.
    const linesToPreview = (
      lines: IPayrollRunItemLine[],
      includeType: boolean,
    ) =>
      lines
        .filter((l) => l.isActive && l.name.trim() && toNumber(l.amount) !== 0)
        .map((l) => ({
          name: l.name,
          amount: toNumber(l.amount),
          earningTypeName: includeType
            ? l.metadata?.earningTypeName
            : undefined,
        }));

    const isBasic = (e: { earningTypeName?: string }) =>
      typeof e.earningTypeName === "string" &&
      e.earningTypeName.trim().toLowerCase() === "basic";
    const sortBasicFirst = <T extends { earningTypeName?: string }>(arr: T[]) =>
      [...arr].sort((a, b) => (isBasic(a) ? 0 : 1) - (isBasic(b) ? 0 : 1));

    const previewEarnings = sortBasicFirst([
      ...linesToPreview(form.earnings, true),
      ...linesToPreview(form.benefits, false),
    ]).map(({ name, amount }) => ({ name, amount }));

    // The dates behind Loss of Pay ride on the server's preview row (B45).
    const lopDetail =
      base.deductions.find((d) => d.lopDetail)?.lopDetail ?? null;
    const regularDeductions = form.deductions
      .filter((l) => l.isActive && l.name.trim() && toNumber(l.amount) !== 0)
      .map((l) => ({
        name: l.name,
        amount: toNumber(l.amount),
        lopDetail: l.referenceType === LOP_REFERENCE_TYPE ? lopDetail : null,
      }));
    const statutoryEmployeeLines = (detail.statutoryLines ?? [])
      .filter((s) => s.party === "EMPLOYEE")
      .map((s) => ({ name: s.name, amount: toNumber(s.amount) }));
    const previewDeductions = [...regularDeductions, ...statutoryEmployeeLines];
    const totalStatutoryEmployee = toNumber(detail.totalStatutoryEmployee);
    const totalEarningsAll = totalEarnings + totalBenefits;
    const totalDeductionsAll = totalDeductions + totalStatutoryEmployee;
    return {
      ...base,
      paymentMode: form.paymentMethod || base.paymentMode,
      employee: {
        ...base.employee,
        bankName: form.bankName || base.employee.bankName,
        accountNumber: form.accountNumber || base.employee.accountNumber,
      },
      earnings: previewEarnings,
      deductions: previewDeductions,
      totalEarnings: totalEarningsAll,
      totalDeductions: totalDeductionsAll,
      netSalary: totalEarningsAll - totalDeductionsAll,
    };
  }, [
    detail,
    form.earnings,
    form.benefits,
    form.deductions,
    form.paymentMethod,
    form.bankName,
    form.accountNumber,
    totalEarnings,
    totalBenefits,
    totalDeductions,
  ]);

  const headerEmp = detail?.header?.employee;
  const fullName = headerEmp?.fullName ?? "—";
  const employeeCode = headerEmp?.employeeCode ?? "";
  const profileImage = headerEmp?.profileImage ?? null;
  const branchName = headerEmp?.branch?.name ?? "—";
  const departmentName = headerEmp?.department?.name ?? "—";
  const nationality = headerEmp?.nationality ?? null;
  const currencyCode = detail?.currency?.code ?? "";
  const breadcrumb = useMemo(
    () => `Payroll / Run #${runId} / Item #${itemId}`,
    [runId, itemId],
  );

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-gray-400">
        {t("company:payrolls.loading_employee_details")}
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-6 text-sm text-gray-400">
        {t("company:payrolls.item_not_found")}
        <Button variant="outline" onClick={handleBack} className="ms-2">
          {t("common:buttons.back")}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-muted p-4 md:p-6 flex flex-col gap-4">
      {/* Top header: cancel + save */}
      <div className="flex items-center justify-between gap-3 rounded-xs border border-border bg-white px-5 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={handleBack}
            className="border border-surface-elevated rounded-xs p-2 w-fit h-fit shrink-0"
          >
            <ChevronLeft className="text-primary size-5 rtl:rotate-180" />
          </button>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-gray-900">
              {t("company:payrolls.employee_detail")}
            </span>
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <span>{t("company:payrolls.home")}</span>
              <span>/</span>
              <MoreHorizontal size={12} />
              <span>/</span>
              <span className="text-gray-700 font-medium truncate">
                {breadcrumb}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isProcessed && (
            <Button
              variant="outline"
              onClick={() => downloadPayslip(itemId)}
              disabled={isDownloading || skip}
            >
              <Download size={14} className="me-1.5" />
              {isDownloading
                ? t("common:states.downloading")
                : t("company:payrolls.download_payslip")}
            </Button>
          )}
          {isReadOnly ? (
            <Button variant="outline" onClick={handleBack}>
              {t("common:buttons.back")}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleBack}>
                {t("common:buttons.cancel")}
              </Button>
              <PermissionButton
                section={PLATFORM_SECTION_ENUM.PAYROLL}
                action="edit"
                mode="disable"
                variant="primary"
                onClick={handleSaveClick}
                disabled={isSaving || !isDirty}
              >
                {isSaving
                  ? t("common:states.saving")
                  : t("company:payrolls.save_changes")}
              </PermissionButton>
            </>
          )}
        </div>
      </div>

      {/* Employee summary + Hold Salary */}
      <div className="rounded-xs border border-border bg-white overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">
              {t("company:payrolls.employee_earning_details")}
            </span>
            {isReadOnly && runStatus && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-warning bg-warning-muted border border-warning/40 rounded-xs px-2 py-0.5">
                {isProcessed
                  ? t("company:payrolls.read_only_payroll_processed")
                  : t("company:payrolls.read_only_until_review")}
              </span>
            )}
          </div>
          {!isReadOnly && detail?.status !== "EXCLUDED" && (
            <ExcludeItemButton runId={runId} itemId={itemId} />
          )}
          {canToggleHold && (
            <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
              {t("company:payrolls.hold_salary_title")}
              <PermissionGuard
                section={PLATFORM_SECTION_ENUM.PAYROLL}
                action="edit"
                mode="disable"
              >
                <Switch
                  checked={form.holdSalary}
                  disabled={isTogglingHold}
                  onCheckedChange={handleToggleHold}
                />
              </PermissionGuard>
            </label>
          )}
        </div>
        <div className="flex items-stretch divide-x divide-border">
          <div className="flex items-center gap-3 px-5 py-4 flex-1 min-w-0">
            <Avatar className="size-12">
              {profileImage && (
                <AvatarImage src={profileImage} alt={fullName} />
              )}
              <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">
                {getInitials(fullName)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-gray-900 truncate">
                {fullName}
              </span>
              <div className="flex items-center gap-1.5 text-[11px] text-gray-400 truncate">
                <span>{employeeCode}</span>
                {nationality?.code && (
                  <>
                    <span className="text-gray-300">·</span>
                    <ReactCountryFlag
                      countryCode={nationality.code}
                      svg
                      style={{
                        width: "0.9em",
                        height: "0.7em",
                        borderRadius: "1px",
                      }}
                    />
                  </>
                )}
                {nationality?.name && <span>{nationality.name}</span>}
              </div>
            </div>
          </div>
          <div className="flex flex-col justify-center px-5 py-4 min-w-[160px]">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {t("common:ui_kit.branch")}
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {branchName}
            </span>
          </div>
          <div className="flex flex-col justify-center px-5 py-4 min-w-[160px]">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {t("company:payrolls.dept")}
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {departmentName}
            </span>
          </div>
        </div>
      </div>

      <Tabs defaultValue="payslip" className="flex flex-col gap-4">
        <TabsList className="w-full justify-start rounded-none bg-transparent border-b border-border p-0 h-auto">
          <TabsTrigger
            value="payslip"
            className="relative rounded-none bg-transparent px-4 py-3 text-sm font-medium text-gray-500 border-b-2 border-transparent data-[state=active]:bg-primary-light data-[state=active]:text-primary data-[state=active]:border-primary data-[state=active]:shadow-none hover:text-primary hover:bg-primary-light transition-colors"
          >
            {t("nav:tabs.payslip")}
          </TabsTrigger>
          <TabsTrigger
            value="history"
            className="relative rounded-none bg-transparent px-4 py-3 text-sm font-medium text-gray-500 border-b-2 border-transparent data-[state=active]:bg-primary-light data-[state=active]:text-primary data-[state=active]:border-primary data-[state=active]:shadow-none hover:text-primary hover:bg-primary-light transition-colors"
          >
            <span className="flex items-center gap-1.5">
              {t("company:payrolls.change_history")}
              {!skip && auditLogCount > 0 && (
                <span className="inline-flex items-center justify-center h-[18px] min-w-[18px] px-1 rounded-full bg-primary text-[10px] font-semibold text-white">
                  {auditLogCount}
                </span>
              )}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payslip">
          {/* Two-column body: form sections + payslip preview */}
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 items-start">
            <div className="flex flex-col gap-4">
              <Section title={t("company:payrolls.payment_details")}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      {t("company:payrolls.payment_method")}
                    </label>
                    {isReadOnly ? (
                      <div className="h-12 border border-input rounded-xs px-3 text-sm bg-gray-50 text-gray-700 flex items-center">
                        {buildPaymentMethodOptions(t).find(
                          (o) => o.value === form.paymentMethod,
                        )?.label ||
                          form.paymentMethod ||
                          "—"}
                      </div>
                    ) : (
                      <Select
                        value={form.paymentMethod || ""}
                        onValueChange={(v) =>
                          setForm((f) => ({
                            ...f,
                            paymentMethod: v as PaymentMethodTypeEnum,
                          }))
                        }
                      >
                        <SelectTrigger className="h-12">
                          <SelectValue
                            placeholder={t("company:payrolls.select_method")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {buildPaymentMethodOptions(t).map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      {t("company:payrolls.currency")}
                    </label>
                    <input
                      type="text"
                      value={currencyCode}
                      readOnly
                      className="h-12 border border-input rounded-xs px-3 text-sm bg-gray-50 text-gray-700"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      {t("company:payrolls.bank_name")}
                    </label>
                    <input
                      type="text"
                      value={form.bankName}
                      readOnly={isReadOnly}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, bankName: e.target.value }))
                      }
                      placeholder={t("company:payrolls.eg_emirates_nbd")}
                      className={cn(
                        "h-12 border border-input rounded-xs px-3 text-sm",
                        isReadOnly
                          ? "bg-gray-50 text-gray-700"
                          : "focus:outline-hidden focus:ring-2 focus:ring-ring",
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      {t("company:payrolls.account_number")}
                    </label>
                    <input
                      type="text"
                      value={form.accountNumber}
                      readOnly={isReadOnly}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          accountNumber: e.target.value
                            .replace(/[^a-zA-Z0-9]/g, "")
                            .toUpperCase(),
                        }))
                      }
                      placeholder={t("company:payrolls.iban_account_number")}
                      className={cn(
                        "h-12 border border-input rounded-xs px-3 text-sm",
                        isReadOnly
                          ? "bg-gray-50 text-gray-700"
                          : "focus:outline-hidden focus:ring-2 focus:ring-ring",
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      {t("company:payrolls.routing_code")}
                    </label>
                    <input
                      type="text"
                      value={form.routingNumber}
                      readOnly={isReadOnly}
                      inputMode="numeric"
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          routingNumber: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                      placeholder={t(
                        "company:payrolls.bank_routing_agent_code",
                      )}
                      className={cn(
                        "h-12 border border-input rounded-xs px-3 text-sm",
                        isReadOnly
                          ? "bg-gray-50 text-gray-700"
                          : "focus:outline-hidden focus:ring-2 focus:ring-ring",
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      {t("company:payrolls.wps_person_id")}
                    </label>
                    <input
                      type="text"
                      value={form.wpsPersonId}
                      readOnly={isReadOnly}
                      inputMode="numeric"
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          wpsPersonId: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                      placeholder={t(
                        "company:payrolls.labour_card_mol_person_id",
                      )}
                      className={cn(
                        "h-12 border border-input rounded-xs px-3 text-sm",
                        isReadOnly
                          ? "bg-gray-50 text-gray-700"
                          : "focus:outline-hidden focus:ring-2 focus:ring-ring",
                      )}
                    />
                  </div>
                </div>
              </Section>

              <div className="flex flex-col">
                <LinesSection
                  label={t("company:payrolls.earnings")}
                  lines={form.earnings}
                  currencyCode={currencyCode}
                  tone="earning"
                  onChange={updateLines("earnings")}
                  bottomFlush
                  readOnly={isReadOnly}
                />
                <TotalsRow
                  label={t("company:payrolls.total_earnings")}
                  currencyCode={currencyCode}
                  amount={totalEarnings}
                  topFlush
                />
              </div>

              <div className="flex flex-col">
                <LinesSection
                  label={t("company:payrolls.benefits")}
                  lines={form.benefits}
                  currencyCode={currencyCode}
                  tone="earning"
                  onChange={updateLines("benefits")}
                  bottomFlush
                  readOnly={isReadOnly}
                />
                <TotalsRow
                  label={t("company:payrolls.total_benefits")}
                  currencyCode={currencyCode}
                  amount={totalBenefits}
                  topFlush
                />
              </div>

              <div className="flex flex-col">
                <LinesSection
                  label={t("company:payrolls.deductions")}
                  lines={form.deductions}
                  currencyCode={currencyCode}
                  tone="deduction"
                  onChange={updateLines("deductions")}
                  bottomFlush
                  readOnly={isReadOnly}
                />
                <TotalsRow
                  label={t("company:payrolls.total_deductions")}
                  currencyCode={currencyCode}
                  amount={totalDeductions}
                  topFlush
                />
                {deductionShareOverHalf !== null && (
                  <p className="mt-3 rounded-xs border border-warning/30 bg-warning-muted px-3 py-2 text-xs text-warning">
                    {t("company:payrolls.deductions_over_half_item", {
                      percent: deductionShareOverHalf,
                    })}
                  </p>
                )}
                {!isReadOnly && (
                  <div className="mt-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                    <label
                      htmlFor="unpaid-days"
                      className="text-[10px] font-semibold uppercase tracking-wider text-gray-500"
                    >
                      {t("company:payrolls.unpaid_days_lop")}
                    </label>
                    <input
                      id="unpaid-days"
                      type="number"
                      min={0}
                      step={0.5}
                      value={form.unpaidLeaveDays}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          unpaidLeaveDays: e.target.value,
                        }))
                      }
                      className="h-9 w-24 rounded-xs border border-input px-2 text-end text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <span className="text-[11px] text-muted-foreground">
                      {toNumber(form.unpaidLeaveDays) !==
                      toNumber(savedForm.unpaidLeaveDays)
                        ? t("company:payrolls.unpaid_days_recalculated_on_save")
                        : t("company:payrolls.unpaid_days_hint")}
                    </span>
                  </div>
                )}
              </div>

              {nationality?.code === "AE" &&
                (detail.statutoryLines?.length ?? 0) > 0 && (
                  <Section
                    title={t("company:payrolls.statutory_auto_calculated")}
                    rightSlot={
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary-light rounded-xs px-2 py-0.5">
                        UAE GPSSA
                      </span>
                    }
                  >
                    <div className="flex flex-col gap-2">
                      <div className="rounded-xs bg-primary-light/40 border border-primary/20 px-3 py-2 text-xs text-primary">
                        {t(
                          "company:payrolls.calculated_automatically_per_uae_gpssa_federal_law_emplo",
                        )}
                      </div>
                      <div className="rounded-xs border border-border">
                        {detail.statutoryLines.map((line) => (
                          <StatutoryRow
                            key={line.id}
                            line={line}
                            currencyCode={currencyCode}
                          />
                        ))}
                      </div>
                    </div>
                  </Section>
                )}

              {/* Net Pay summary */}
              <TotalsRow
                label={t("company:payrolls.net_pay")}
                currencyCode={currencyCode}
                amount={
                  totalEarnings +
                  totalBenefits -
                  totalDeductions -
                  totalStatutoryEmployee
                }
              />
            </div>

            {/* Live payslip preview pane */}
            <aside className="rounded-xs bg-gray-100 p-4 lg:sticky lg:top-4">
              {livePreview ? (
                <div
                  className="w-full overflow-hidden"
                  style={{ aspectRatio: "210 / 297" }}
                >
                  <PayslipPreviewCard preview={livePreview} />
                </div>
              ) : (
                <div className="rounded-xs border border-border bg-white px-5 py-4 text-xs text-gray-400">
                  {t("company:payrolls.payslip_preview_unavailable")}
                </div>
              )}
            </aside>
          </div>
        </TabsContent>

        <TabsContent value="history">
          {!skip && <ChangeHistory runId={runId} itemId={itemId} />}
        </TabsContent>
      </Tabs>

      <SaveReasonDialog
        open={reasonDialogOpen}
        isSaving={isSaving}
        onCancel={() => setReasonDialogOpen(false)}
        onConfirm={handleSaveWithReason}
      />

      <SaveReasonDialog
        open={holdReasonOpen}
        isSaving={isHolding}
        onCancel={() => setHoldReasonOpen(false)}
        onConfirm={performHold}
      />

      {blocker.state === "blocked" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-sm shadow-xl max-w-sm w-full">
            <div className="flex items-center justify-center h-16 w-16 rounded-full mx-auto mb-4 bg-orange-100 text-orange-500">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-center text-orange-600">
              {t("company:payrolls.unsaved_changes")}
            </h3>
            <p className="py-3 text-gray-600 text-center text-sm">
              {t(
                "company:payrolls.you_have_unsaved_changes_do_you_want_to_discard_changes_",
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => blocker.reset?.()}
                className="px-4 py-2 border border-border rounded-xs grow text-center text-border-strong"
              >
                {t("company:payrolls.stay_and_save")}
              </button>
              <button
                onClick={() => blocker.proceed?.()}
                className="px-4 py-2 bg-red-500 text-white rounded-xs grow text-center"
              >
                {t("company:payrolls.discard_and_leave")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditPayrollRunItem;
